import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/react'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { buildChatReferenceFromAgentReference, buildRiaContextFromAgentReference, trackRiaSourceViewerOpened } from '@/analytics/utils/ria-source-analytics'
import { RiaSourceTriggerLocation } from '@/analytics/enums/ria.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import type { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { useAppDispatch } from '@/store/store-hooks'
import type { ClientAgentReference } from '../store/schemas'
import { openAgentReferenceViewer } from '../util/open-agent-reference-viewer'

type OpenReferenceOptions = {
  reference: ClientAgentReference
  matterId: string | null
  triggerLocation: RiaSourceTriggerLocation
  citationHighlightIndex?: number
  defaultTab?: CaseLawReferenceTab
}

function buildAgentReferenceOpenTelemetry(args: { reference: ClientAgentReference; matterId: string | null; triggerLocation: RiaSourceTriggerLocation }) {
  const { reference, matterId, triggerLocation } = args

  return {
    tags: {
      contentViewerFeature: 'agent_reference',
      referenceType: reference.type,
      triggerLocation,
    },
    extra: {
      referenceId: reference.reference.id,
      citationId: reference.reference.citation_id,
      turnId: reference.reference.turn_id,
      conversationId: reference.conversation_id,
      matterId,
    },
  }
}

function captureAgentReferenceOpenFailure(args: {
  reference: ClientAgentReference
  matterId: string | null
  triggerLocation: RiaSourceTriggerLocation
  reason: string
}) {
  const { reason, ...telemetryArgs } = args
  const telemetry = buildAgentReferenceOpenTelemetry(telemetryArgs)

  Sentry.captureMessage('Agent reference viewer open failed', {
    level: 'warning',
    tags: {
      ...telemetry.tags,
      reason,
    },
    extra: telemetry.extra,
  })
}

export function useOpenAgentReferenceViewer() {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const [isOpening, setIsOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const openReference = useCallback(
    async (options: OpenReferenceOptions): Promise<boolean> => {
      const { reference, matterId, triggerLocation, citationHighlightIndex, defaultTab } = options
      setIsOpening(true)
      setError(null)

      try {
        const result = await openAgentReferenceViewer({ reference, matterId, citationHighlightIndex, defaultTab })
        if (!result.opened) {
          captureAgentReferenceOpenFailure({ reference, matterId, triggerLocation, reason: result.reason })
          setError(result.error)
          return false
        }

        dispatch(ContentViewerActions.setContentViewerSession({ session: result.session }))
        dispatch(
          AgentConversationsActions.setConversationActiveContentViewerSession({
            conversationId: reference.conversation_id,
            session: result.session,
          })
        )

        const context = buildRiaContextFromAgentReference(reference, matterId)
        trackRiaSourceViewerOpened({
          trackEvent,
          context,
          reference: buildChatReferenceFromAgentReference(reference),
          triggerLocation,
        })

        return true
      } catch (err) {
        Sentry.captureException(err, buildAgentReferenceOpenTelemetry({ reference, matterId, triggerLocation }))
        setError('Could not load this reference.')
        return false
      } finally {
        setIsOpening(false)
      }
    },
    [dispatch, trackEvent]
  )

  return {
    openReference,
    isOpening,
    error,
    clearError,
  }
}
